
 import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  PlusIcon, 
  ShareIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  TrashIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  DocumentCheckIcon,
  XMarkIcon,
  EnvelopeIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import Layout from './Layout';
import FormBuilder from './FormBuilder';
import PermissionGate from './PermissionGate';
import { 
  getAllForms, 
  getAllSubmissions,
  createForm,
  updateForm,
  deleteForm, 
  copyFormUrlToClipboard,
  updateSubmissionStatus,
  deleteSubmission,
  getStatusColor,
  getStatusLabel
} from '../services/formApi';
import { leadsAPI } from '../services/api';
import { Form, FormSubmission, FormSubmissionData } from '../types/Form';
import { useBulkSelection } from '../hooks/useBulkSelection';
import BulkActionsToolbar, { BulkActionAvailability } from './forms/BulkActionsToolbar';
import BulkDeleteModal from './forms/BulkDeleteModal';
import BulkCreateLeadsModal from './forms/BulkCreateLeadsModal';
import BulkAttachToLeadModal from './forms/BulkAttachToLeadModal';

// Utility functions
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'submitted':
      return <ClockIcon className="w-4 h-4" />;
    case 'under_review':
      return <ClockIcon className="w-4 h-4" />;
    case 'approved':
      return <CheckCircleIcon className="w-4 h-4" />;
    case 'rejected':
      return <XCircleIcon className="w-4 h-4" />;
    case 'attached':
      return <DocumentTextIcon className="w-4 h-4" />;
    default:
      return <ClockIcon className="w-4 h-4" />;
  }
};

const renderSubmissionData = (submission: FormSubmission) => {
  const data = submission.submissionData;
  
  // Helper function to format field label
  const formatLabel = (key: string): string => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
      .replace(/([A-Z])/g, ' $1')
      .trim();
  };
  
  // Helper function to format value based on key/content
  const formatValue = (key: string, value: any): string => {
    if (value === null || value === undefined || value === '') return '—';
    
    // Handle arrays
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    
    // Handle dates (common field names)
    if (key.toLowerCase().includes('date') || key.toLowerCase().includes('dob')) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
        }
      } catch {
        // Not a valid date, continue
      }
    }
    
    // Handle emails
    if (key.toLowerCase().includes('email')) {
      return String(value);
    }
    
    // Handle URLs
    if (key.toLowerCase().includes('url') || key.toLowerCase().includes('website')) {
      return String(value);
    }
    
    // Handle phone numbers
    if (key.toLowerCase().includes('phone') || key.toLowerCase().includes('mobile') || key.toLowerCase().includes('tel')) {
      return String(value);
    }
    
    return String(value);
  };
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {Object.entries(data).map(([key, value]) => {
        if (value === null || value === undefined || value === '') return null;
        
        const formattedValue = formatValue(key, value);
        if (formattedValue === '—') return null;
        
        return (
          <div key={key} className="bg-white p-4 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
            <div className="mb-1.5">
              <span className="text-sm font-bold text-gray-900">{formatLabel(key)}</span>
              <span className="text-xs text-gray-500 ml-2">({key})</span>
            </div>
            <div className="text-sm text-gray-700 break-words">
              {formattedValue}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Forms: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [forms, setForms] = useState<Form[]>([]);
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [formFilter, setFormFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState<'submitted_at' | 'status'>('submitted_at');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [editingForm, setEditingForm] = useState<Form | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState<Form | null>(null);
  const [showSubmissionDeleteModal, setShowSubmissionDeleteModal] = useState<FormSubmission | null>(null);
  const [showStatusModal, setShowStatusModal] = useState<FormSubmission | null>(null);
  const [showReviewModal, setShowReviewModal] = useState<FormSubmission | null>(null);
  const [showSubmissionModal, setShowSubmissionModal] = useState<FormSubmission | null>(null);
  const [showFormModal, setShowFormModal] = useState<Form | null>(null);
  const [showAttachToLeadModal, setShowAttachToLeadModal] = useState<FormSubmission | null>(null);
  const [leads, setLeads] = useState<any[]>([]);
  const [leadSearchTerm, setLeadSearchTerm] = useState('');
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  const [copySuccess, setCopySuccess] = useState<number | null>(null);
  // Persist tab in URL so it survives refresh
  const tabParam = searchParams.get('tab');
  const viewMode: 'submissions' | 'forms' = (tabParam === 'forms' || tabParam === 'submissions') ? tabParam : 'submissions';
  const setViewMode = useCallback((mode: 'submissions' | 'forms') => {
    setSearchParams({ tab: mode });
  }, [setSearchParams]);
  
  // Smart lead matching states
  const [showLeadMatchModal, setShowLeadMatchModal] = useState<FormSubmission | null>(null);
  const [matchingLeads, setMatchingLeads] = useState<any[]>([]);
  const [isSearchingLeads, setIsSearchingLeads] = useState(false);
  const [showCreateLeadModal, setShowCreateLeadModal] = useState(false);
  
  // Duplicate email warning states
  const [showDuplicateEmailModal, setShowDuplicateEmailModal] = useState(false);
  const [duplicateEmailData, setDuplicateEmailData] = useState<{formData: any, existingLead: any} | null>(null);
  const [editedEmail, setEditedEmail] = useState('');

  // Bulk operations state
  const bulkSelection = useBulkSelection({
    maxSelection: 1000,
    onSelectionChange: (selected) => {
      // Clear error when selection changes
      if (error) setError(null);
    }
  });

  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showBulkCreateLeadsModal, setShowBulkCreateLeadsModal] = useState(false);
  const [showBulkAttachModal, setShowBulkAttachModal] = useState(false);
  const [bulkOperationInProgress, setBulkOperationInProgress] = useState(false);

  // Calculate bulk action availability
  const bulkActionAvailability: BulkActionAvailability = useMemo(() => {
    const selected = submissions.filter(s => bulkSelection.selectedIds.has(s.id));
    
    if (selected.length === 0) {
      return {
        canReview: false,
        canCreateLeads: false,
        canAttach: false,
        canDelete: false,
        hasMixedStatuses: false,
        statusBreakdown: {}
      };
    }

    const statuses = selected.map(s => s.status);
    const uniqueStatuses = new Set(statuses);
    
    return {
      canReview: selected.every(s => s.status === 'submitted' || s.status === 'under_review'),
      canCreateLeads: selected.every(s => s.status === 'approved' && !s.leadId),
      canAttach: selected.every(s => s.status === 'approved'),
      canDelete: selected.length > 0,
      hasMixedStatuses: uniqueStatuses.size > 1,
      statusBreakdown: selected.reduce((acc, s) => {
        acc[s.status] = (acc[s.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>)
    };
  }, [submissions, bulkSelection.selectedIds]);

  useEffect(() => {
    if (viewMode === 'submissions') {
      loadSubmissions();
      // Also load forms for the filter dropdown
      if (forms.length === 0) {
        loadForms();
      }
    } else {
      loadForms();
    }
  }, [currentPage, searchTerm, statusFilter, formFilter, startDate, endDate, sortBy, sortOrder, viewMode]);

  // Keyboard shortcuts for bulk operations
  useEffect(() => {
    const handleKeyboard = (event: KeyboardEvent) => {
      // Only handle shortcuts in submissions view
      if (viewMode !== 'submissions') return;
      
      // Don't trigger if user is typing in an input
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modKey = isMac ? event.metaKey : event.ctrlKey;

      // Ctrl/Cmd + A - Select All
      if (modKey && event.key === 'a' && submissions.length > 0) {
        event.preventDefault();
        bulkSelection.selectAll(submissions.map(s => s.id));
      }

      // Ctrl/Cmd + D - Deselect All
      if (modKey && event.key === 'd' && bulkSelection.selectedCount > 0) {
        event.preventDefault();
        bulkSelection.clearSelection();
      }

      // Escape - Clear Selection
      if (event.key === 'Escape' && bulkSelection.selectedCount > 0) {
        event.preventDefault();
        bulkSelection.clearSelection();
      }

      // Delete key - Bulk Delete (if items selected)
      if (event.key === 'Delete' && bulkSelection.selectedCount > 0 && bulkActionAvailability.canDelete) {
        event.preventDefault();
        setShowBulkDeleteModal(true);
      }
    };

    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [viewMode, submissions, bulkSelection, bulkActionAvailability]);

  const loadForms = async () => {
    try {
      setLoading(true);
      const response = await getAllForms({
        page: currentPage,
        limit: 20,
        search: searchTerm,
        isActive: statusFilter === 'all' ? undefined : statusFilter === 'active',
        sortBy: 'created_at',
        sortOrder: 'DESC'
      });

      setForms(response.data);
      setTotalPages(response.pagination.totalPages);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const response = await getAllSubmissions({
        page: currentPage,
        limit: 20,
        search: searchTerm,
        status: statusFilter === 'all' ? undefined : statusFilter,
        formId: formFilter === 'all' ? undefined : parseInt(formFilter),
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        sortBy,
        sortOrder
      });
      
      setSubmissions(response.data);
      setTotalPages(response.pagination.totalPages);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };


  const handleCreateCustomForm = () => {
    setEditingForm(undefined);
    setShowFormBuilder(true);
  };

  const handleEditForm = (form: Form) => {
    setEditingForm(form);
    setShowFormBuilder(true);
  };

  const handleSaveForm = async (formData: Partial<Form>) => {
    try {
      setLoading(true);
      setError(null);
      
      // Frontend validation before API call
      if (!formData.name || formData.name.trim() === '') {
        setError('Form name is required');
        return; // Don't close FormBuilder
      }
      
      if (!formData.formStructure || !formData.formStructure.sections || formData.formStructure.sections.length === 0) {
        setError('Form must have at least one section');
        return;
      }
      
      // Count total fields
      let fieldCount = 0;
      formData.formStructure.sections.forEach(section => {
        if (section.fields) fieldCount += section.fields.length;
        if (section.subsections) {
          section.subsections.forEach(subsection => {
            fieldCount += subsection.fields.length;
          });
        }
      });
      
      if (fieldCount === 0) {
        setError('Form must have at least one field');
        return;
      }
      
      if (editingForm && editingForm.id > 0) {
        // Update existing form (only if it has a real ID)
        await updateForm(editingForm.id, {
          name: formData.name,
          description: formData.description,
          formStructure: formData.formStructure,
          defaultLeadSourceId: formData.defaultLeadSourceId
        });
      } else {
        // Create new form
        const createData = {
          name: formData.name,
          description: formData.description || '',
          formStructure: formData.formStructure,
          formType: editingForm && editingForm.id === 0 ? 'student_application_custom' : 'custom',
          isActive: true,
          isPublic: true,
          allowMultipleSubmissions: true,
          defaultLeadSourceId: formData.defaultLeadSourceId || null
        };
        await createForm(createData);
      }
      
      await loadForms(); // Refresh the list
      setShowFormBuilder(false);
      setEditingForm(undefined);
      setError(null);
      
      // Success notification (you could use a toast library here)
      console.log('✅ Form saved successfully!');
      
    } catch (err: any) {
      // Don't close FormBuilder on error - let user fix issues
      setError(err.response?.data?.message || err.message || 'Failed to save form');
      console.error('Error saving form:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseFormBuilder = () => {
    setShowFormBuilder(false);
    setEditingForm(undefined);
  };


  const handleDeleteForm = async (form: Form) => {
    try {
      await deleteForm(form.id);
      await loadForms();
      setShowDeleteModal(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete form');
    }
  };

  const handleCopyLink = async (form: Form) => {
    const success = await copyFormUrlToClipboard(form.publicToken);
    if (success) {
      setCopySuccess(form.id);
      setTimeout(() => setCopySuccess(null), 2000);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (status: 'all' | 'active' | 'inactive') => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleStatusUpdate = async (submission: FormSubmission, status: string, notes: string = '') => {
    try {
      await updateSubmissionStatus(submission.id, { status: status as any, notes });
      await loadSubmissions();
      setShowStatusModal(null);
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
    }
  };

  const handleDeleteSubmission = async (submission: FormSubmission) => {
    try {
      await deleteSubmission(submission.id);
      await loadSubmissions();
      setShowSubmissionDeleteModal(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete submission');
    }
  };

  const handleReviewSubmission = async (
    submission: FormSubmission, 
    action: 'approve' | 'draft' | 'reject',
    reviewedData?: FormSubmissionData,
    changesDescription?: string,
    reviewComments?: string,
    rejectionReason?: string
  ) => {
    try {
      let newStatus: 'approved' | 'under_review' | 'rejected';
      switch (action) {
        case 'approve':
          newStatus = 'approved';
          break;
        case 'draft':
          newStatus = 'under_review';
          break;
        case 'reject':
          newStatus = 'rejected';
          break;
      }

      await updateSubmissionStatus(submission.id, {
        status: newStatus,
        reviewedData,
        changesDescription,
        reviewComments,
        rejectionReason
      });
      
      await loadSubmissions();
      setShowReviewModal(null);
    } catch (err: any) {
      setError(err.message || 'Failed to review submission');
    }
  };

  const loadLeads = async () => {
    try {
      const response = await leadsAPI.getLeads();
      setLeads(response.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load leads');
    }
  };

  // Helper function to normalize form data field names for backend matching
  // Ensures field names match what backend expects (handles camelCase, snake_case, spaced names)
  const normalizeFormDataForSearch = (formData: any): any => {
    if (!formData || typeof formData !== 'object') return formData;
    
    const normalized: any = {};
    
    // Map all possible field name variations to standard keys
    const fieldMappings: { [key: string]: string[] } = {
      'first_name': ['firstName', 'first_name', 'First Name'],
      'last_name': ['lastName', 'last_name', 'Last Name'],
      'email': ['email', 'Email'],
      'phone': ['phone', 'phoneNumber', 'Phone Number'],
      'mobile_number': ['mobile_no', 'mobileNumber', 'Mobile Number'],
      'company': ['company', 'Company']
    };
    
    // Extract values using all possible field name variations
    Object.keys(fieldMappings).forEach(standardKey => {
      const variations = fieldMappings[standardKey];
      for (const variation of variations) {
        if (formData[variation] !== undefined && formData[variation] !== null && formData[variation] !== '') {
          normalized[standardKey] = formData[variation];
          break; // Use first found value
        }
      }
    });
    
    // Include all other fields as-is
    Object.keys(formData).forEach(key => {
      if (!normalized[key] && formData[key] !== undefined && formData[key] !== null && formData[key] !== '') {
        normalized[key] = formData[key];
      }
    });
    
    return normalized;
  };

  const handleAttachToLead = async (submission: FormSubmission) => {
    try {
      setIsSearchingLeads(true);
      
      // Normalize form data before searching (ensures field names match backend expectations)
      const normalizedFormData = normalizeFormDataForSearch(submission.submissionData);
      
      // Search for matching leads based on normalized form data
      const response = await leadsAPI.searchMatchingLeads({ formData: normalizedFormData });
      const matches = response.data || [];
      
      // Always show the lead match modal with create option
      setMatchingLeads(matches);
      setShowLeadMatchModal(submission);
      setShowCreateLeadModal(true);
    } catch (err: any) {
      setError(err.message || 'Failed to search for matching leads');
    } finally {
      setIsSearchingLeads(false);
    }
  };

  const handleAttachToExistingLead = async (leadId: number) => {
    if (!showLeadMatchModal) return;
    
    try {
      // Update the submission status to 'attached' and attach to lead
      await updateSubmissionStatus(showLeadMatchModal.id, {
        status: 'attached',
        leadId: leadId
      });
      
      // Refresh the submissions list to show the updated status
      await loadSubmissions();
      
      setShowLeadMatchModal(null);
      setMatchingLeads([]);
      setShowCreateLeadModal(false);
    } catch (err: any) {
      setError(err.message || 'Failed to attach to lead');
    }
  };

  // Helper function to extract email from form data (matches backend logic)
  const extractEmail = (formData: any): string => {
    return (formData.email || formData.Email || formData['Email'] || '').trim();
  };

  const handleCreateNewLead = async () => {
    if (!showLeadMatchModal) return;
    
    try {
      const formData = showLeadMatchModal.submissionData;
      const email = extractEmail(formData);
      
      // Check for duplicate email if email exists
      if (email) {
        const duplicateCheck = await leadsAPI.checkDuplicateEmail(email);
        
        if (duplicateCheck.isDuplicate) {
          // Show duplicate email warning modal
          setDuplicateEmailData({
            formData: formData,
            existingLead: duplicateCheck.existingLead
          });
          setEditedEmail(email);
          setShowDuplicateEmailModal(true);
          return;
        }
      }
      
      // No duplicate email, create lead directly
      await createLeadWithData(formData);
    } catch (err: any) {
      setError(err.message || 'Failed to create new lead');
    }
  };

  const createLeadWithData = async (formData: any) => {
    try {
      // Create new lead from form data
      const response = await leadsAPI.createLeadFromForm({
        formData: formData,
        formSubmissionId: showLeadMatchModal?.id
      });
      
      // Refresh the submissions list to show the updated status
      await loadSubmissions();
      
      setShowLeadMatchModal(null);
      setMatchingLeads([]);
      setShowCreateLeadModal(false);
      setShowDuplicateEmailModal(false);
      setDuplicateEmailData(null);
    } catch (err: any) {
      setError(err.message || 'Failed to create new lead');
    }
  };

  const handleDuplicateEmailContinue = async () => {
    if (!duplicateEmailData) return;
    
    // Update the form data with the edited email
    const updatedFormData = {
      ...duplicateEmailData.formData,
      email: editedEmail
    };
    
    await createLeadWithData(updatedFormData);
  };

  const handleDuplicateEmailAttachToExisting = async () => {
    if (!duplicateEmailData || !showLeadMatchModal) return;
    
    try {
      // Attach to existing lead instead of creating new one
      await updateSubmissionStatus(showLeadMatchModal.id, {
        status: 'attached',
        leadId: duplicateEmailData.existingLead.id
      });
      
      // Refresh the submissions list to show the updated status
      await loadSubmissions();
      
      setShowLeadMatchModal(null);
      setMatchingLeads([]);
      setShowCreateLeadModal(false);
      setShowDuplicateEmailModal(false);
      setDuplicateEmailData(null);
    } catch (err: any) {
      setError(err.message || 'Failed to attach to existing lead');
    }
  };

  const handleSkipLeadMatching = () => {
    // Show the regular attach to lead modal
    setShowAttachToLeadModal(showLeadMatchModal);
    setShowLeadMatchModal(null);
    setMatchingLeads([]);
    setShowCreateLeadModal(false);
  };

  // ==================== BULK OPERATIONS ====================

  // Bulk Delete
  const handleBulkDelete = useCallback(async () => {
    const selected = submissions.filter(s => bulkSelection.selectedIds.has(s.id));
    
    if (selected.length === 0) return;
    
    setBulkOperationInProgress(true);
    try {
      // Delete submissions one by one (could be optimized with bulk API endpoint)
      for (const submission of selected) {
        await deleteSubmission(submission.id);
      }
      
      await loadSubmissions();
      bulkSelection.clearSelection();
    } catch (err: any) {
      setError(err.message || 'Failed to delete submissions');
    } finally {
      setBulkOperationInProgress(false);
    }
  }, [submissions, bulkSelection, loadSubmissions]);

  // Bulk Create Leads
  const handleBulkCreateLeads = useCallback(async (
    onProgress: (current: number, total: number, result: any) => void
  ) => {
    const selected = submissions.filter(s => bulkSelection.selectedIds.has(s.id));
    const results: any[] = [];
    
    for (let i = 0; i < selected.length; i++) {
      const submission = selected[i];
      
      try {
        onProgress(i, selected.length, {
          submissionId: submission.id,
          submissionName: submission.displayInfo?.name || submission.submitterName || 'Anonymous',
          status: 'processing' as const
        });
        
        await leadsAPI.createLeadFromForm({
          formData: submission.submissionData,
          formSubmissionId: submission.id
        });
        
        results.push({
          submissionId: submission.id,
          submissionName: submission.displayInfo?.name || submission.submitterName || 'Anonymous',
          status: 'success' as const,
          leadId: undefined
        });
        
        onProgress(i + 1, selected.length, {
          submissionId: submission.id,
          submissionName: submission.displayInfo?.name || submission.submitterName || 'Anonymous',
          status: 'success' as const,
          leadId: undefined
        });
      } catch (err: any) {
        results.push({
          submissionId: submission.id,
          submissionName: submission.displayInfo?.name || submission.submitterName || 'Anonymous',
          status: 'error' as const,
          error: err.response?.data?.message || err.message || 'Failed to create lead'
        });
        
        onProgress(i + 1, selected.length, {
          submissionId: submission.id,
          submissionName: submission.displayInfo?.name || submission.submitterName || 'Anonymous',
          status: 'error' as const,
          error: err.response?.data?.message || err.message || 'Failed to create lead'
        });
      }
    }
    
    await loadSubmissions();
    bulkSelection.clearSelection();
    return results;
  }, [submissions, bulkSelection, loadSubmissions]);


  // Bulk Attach to Single Lead
  const handleBulkAttachToSingleLead = useCallback(async (leadId: number) => {
    const selected = submissions.filter(s => bulkSelection.selectedIds.has(s.id));
    
    for (const submission of selected) {
      try {
        await updateSubmissionStatus(submission.id, {
          status: 'attached',
          leadId: leadId
        });
      } catch (err) {
        console.error('Failed to attach submission:', submission.id, err);
      }
    }
    
    await loadSubmissions();
    bulkSelection.clearSelection();
  }, [submissions, bulkSelection, loadSubmissions]);

  // Bulk Attach Individual (each submission to different lead)
  const handleBulkAttachIndividual = useCallback(async (mappings: Array<{submissionId: number; leadId: number | null}>) => {
    for (const mapping of mappings) {
      if (mapping.leadId) {
        try {
          await updateSubmissionStatus(mapping.submissionId, {
            status: 'attached',
            leadId: mapping.leadId
          });
        } catch (err) {
          console.error('Failed to attach submission:', mapping.submissionId, err);
        }
      }
    }
    
    await loadSubmissions();
    bulkSelection.clearSelection();
  }, [bulkSelection, loadSubmissions]);

  // Load leads for bulk attach modal
  const handleLoadLeadsForBulkAttach = useCallback(async (search: string) => {
    try {
      const response = await leadsAPI.getLeads();
      const allLeads = response.data || [];
      
      // Filter leads by search term on client side
      if (search) {
        const searchLower = search.toLowerCase();
        return allLeads.filter((lead: any) =>
          lead.fullName?.toLowerCase().includes(searchLower) ||
          lead.email?.toLowerCase().includes(searchLower) ||
          lead.company?.toLowerCase().includes(searchLower)
        );
      }
      
      return allLeads;
    } catch (err) {
      console.error('Failed to load leads:', err);
      return [];
    }
  }, []);

  const filteredLeads = leads.filter(lead => 
    lead.fullName?.toLowerCase().includes(leadSearchTerm.toLowerCase()) ||
    lead.email?.toLowerCase().includes(leadSearchTerm.toLowerCase()) ||
    lead.company?.toLowerCase().includes(leadSearchTerm.toLowerCase())
  );


  if (loading && (viewMode === 'submissions' ? submissions.length === 0 : forms.length === 0)) {
    return (
      <Layout title="Forms" subtitle="Loading...">
        <div className="flex items-center justify-center py-12">
          <div>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading forms...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout 
      title={viewMode === 'submissions' ? "Form Submissions" : "Student Application Forms"} 
      subtitle={viewMode === 'submissions' ? "View and manage all form submissions" : "Create and manage forms for student applications"}
    >
      <div className="w-full">
        {/* Integrated Header - Part of Table Container */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
          {/* Top Bar - Title and View Toggle */}
          <div className="px-6 py-3 border-b border-gray-100 flex justify-between items-center bg-gray-50">
            <div className="flex items-center space-x-3">
              <h1 className="text-lg font-bold text-gray-900">
                {viewMode === 'submissions' ? 'Form Submissions' : 'Forms'}
              </h1>
              <span className="px-2.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">
                {viewMode === 'submissions' ? submissions.length : forms.length}
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* View Mode Toggle */}
              <div className="flex bg-white border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('submissions')}
                  className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                    viewMode === 'submissions'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Submissions
                </button>
                <button
                  onClick={() => setViewMode('forms')}
                  className={`px-4 py-1.5 text-sm font-medium transition-colors border-l border-gray-300 ${
                    viewMode === 'forms'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Forms
                </button>
              </div>
              
              {viewMode === 'forms' && (
                <PermissionGate module="forms" permission="create">
                  <button
                    onClick={handleCreateCustomForm}
                    className="bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 flex items-center space-x-1.5 transition-colors text-sm font-medium"
                  >
                    <PlusIcon className="w-4 h-4" />
                    <span>Create Form</span>
                  </button>
                </PermissionGate>
              )}
            </div>
          </div>

          {/* Filter Bar - Integrated */}
          <div className="px-6 py-2.5 bg-white border-b border-gray-100 flex items-center gap-2">
            {/* Search */}
            <div className="flex-1 max-w-md relative">
              <svg className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder={viewMode === 'submissions' ? "Search..." : "Search forms..."}
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            {viewMode === 'submissions' ? (
              <>
                {/* Form Filter */}
                <select
                  value={formFilter}
                  onChange={(e) => {
                    setFormFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Forms</option>
                  {forms.map((form) => (
                    <option key={form.id} value={form.id.toString()}>
                      {form.name}
                    </option>
                  ))}
                </select>
                
                {/* Status Filter - Fixed Order */}
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="submitted">Submitted</option>
                  <option value="approved">Approved</option>
                  <option value="attached">Attached</option>
                  <option value="under_review">Under Review</option>
                  <option value="rejected">Rejected</option>
                </select>

                {/* Date Range Filter */}
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Start Date"
                />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="End Date"
                />

                {/* Clear Filters Button */}
                {(searchTerm || statusFilter !== 'all' || formFilter !== 'all' || startDate || endDate) && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setStatusFilter('all');
                      setFormFilter('all');
                      setStartDate('');
                      setEndDate('');
                      setCurrentPage(1);
                    }}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Clear All
                  </button>
                )}
              </>
            ) : (
            <div className="flex gap-2">
              <button
                onClick={() => handleFilterChange('all')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                    statusFilter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                All
              </button>
              <button
                onClick={() => handleFilterChange('active')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                    statusFilter === 'active'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                Active
              </button>
              <button
                onClick={() => handleFilterChange('inactive')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                    statusFilter === 'inactive'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                Inactive
              </button>
            </div>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Actions Toolbar */}
        {viewMode === 'submissions' && (
          <BulkActionsToolbar
            selectedCount={bulkSelection.selectedCount}
            onClearSelection={bulkSelection.clearSelection}
            availability={bulkActionAvailability}
            onBulkReview={() => {
              // TODO: Implement bulk review
              alert('Bulk review feature coming soon!');
            }}
            onBulkCreateLeads={() => setShowBulkCreateLeadsModal(true)}
            onBulkAttach={() => setShowBulkAttachModal(true)}
            onBulkDelete={() => setShowBulkDeleteModal(true)}
            inProgress={bulkOperationInProgress}
          />
        )}

        {/* Main Content - Continues from Header */}
        <div className="bg-white rounded-b-lg shadow-sm border border-gray-200 border-t-0 overflow-hidden">
          {viewMode === 'submissions' ? (
            // Submissions View
            submissions.length === 0 ? (
              <div className="text-center py-12">
                <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No submissions found</h3>
                <p className="text-gray-600 mb-4">
                  {searchTerm || statusFilter !== 'all' || formFilter !== 'all'
                    ? 'Try adjusting your search or filter criteria.'
                    : 'No form submissions have been made yet.'
                  }
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2.5 text-left w-10">
                        <input
                          type="checkbox"
                          checked={bulkSelection.selectMode === 'all'}
                          onChange={(e) => {
                            if (e.target.checked) {
                              bulkSelection.selectAll(submissions.map(s => s.id));
                            } else {
                              bulkSelection.clearSelection();
                            }
                          }}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Form
                      </th>
                      <th 
                        className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                        onClick={() => {
                          if (sortBy === 'submitted_at') {
                            setSortOrder(sortOrder === 'DESC' ? 'ASC' : 'DESC');
                          } else {
                            setSortBy('submitted_at');
                            setSortOrder('DESC');
                          }
                        }}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Submitted At</span>
                          {sortBy === 'submitted_at' && (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {sortOrder === 'DESC' ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              )}
                            </svg>
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                        onClick={() => {
                          if (sortBy === 'status') {
                            setSortOrder(sortOrder === 'DESC' ? 'ASC' : 'DESC');
                          } else {
                            setSortBy('status');
                            setSortOrder('ASC');
                          }
                        }}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Status</span>
                          {sortBy === 'status' && (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {sortOrder === 'DESC' ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              )}
                            </svg>
                          )}
                        </div>
                      </th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {submissions.map((submission) => (
                      <tr
                        key={submission.id}
                        className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                          bulkSelection.isSelected(submission.id) ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => setShowSubmissionModal(submission)}
                      >
                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={bulkSelection.isSelected(submission.id)}
                            onChange={() => bulkSelection.toggleSelection(submission.id)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center space-x-2">
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                              {(submission.displayInfo?.name || submission.submitterName || 'A').charAt(0).toUpperCase()}
                            </div>
                            <span className="text-sm font-medium text-gray-900 truncate max-w-xs">
                              {submission.displayInfo?.name || submission.submitterName || 'Anonymous'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <span className="text-sm text-gray-700 truncate block max-w-xs">
                            {submission.displayInfo?.email || submission.submitterEmail}
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span className="text-sm text-gray-700 truncate block max-w-xs">
                            {submission.formName}
                          </span>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <div className="text-sm text-gray-700">
                            <div className="font-medium">{new Date(submission.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                            <div className="text-xs text-gray-500">{new Date(submission.submittedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</div>
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColor(submission.status)}`}>
                            {getStatusIcon(submission.status)}
                            <span className="ml-1.5">{getStatusLabel(submission.status)}</span>
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end space-x-1.5">
                            {(submission.status === 'submitted' || submission.status === 'under_review') && (
                              <button
                                onClick={() => setShowReviewModal(submission)}
                                className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors"
                                title="Review"
                              >
                                <DocumentCheckIcon className="w-3.5 h-3.5 mr-1" />
                                Review
                              </button>
                            )}
                            
                            {submission.status === 'approved' && (
                              <button
                                onClick={() => handleAttachToLead(submission)}
                                disabled={isSearchingLeads}
                                className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors disabled:opacity-50"
                                title="Attach"
                              >
                                <DocumentTextIcon className="w-3.5 h-3.5 mr-1" />
                                Attach
                              </button>
                            )}
                            
                            <PermissionGate module="forms" permission="delete">
                              <button
                                onClick={() => setShowSubmissionDeleteModal(submission)}
                                className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
                                title="Delete"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </PermissionGate>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            // Forms View
            forms.length === 0 ? (
            <div className="text-center py-12">
              <DocumentDuplicateIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No forms found</h3>
              <p className="text-gray-600 mb-4">Get started by creating your first form.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Form Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Submissions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {forms.map((form) => (
                    <tr 
                      key={form.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setShowFormModal(form)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{form.name}</div>
                          {form.description && (
                            <div className="text-sm text-gray-500">{form.description}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {form.formType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            form.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {form.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <ChartBarIcon className="w-4 h-4 text-gray-400 mr-1" />
                          {form.submissionCount || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(form.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                          

                          {/* Form Settings */}
                          <PermissionGate module="forms" permission="edit">
                            <button
                              onClick={() => handleEditForm(form)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Form Settings"
                            >
                              <Cog6ToothIcon className="w-4 h-4" />
                            </button>
                          </PermissionGate>

                          {/* Share Form */}
                          <PermissionGate module="forms" permission="view">
                            <button
                              onClick={() => handleCopyLink(form)}
                              className="text-green-600 hover:text-green-900"
                              title="Copy Share Link"
                            >
                              {copySuccess === form.id ? (
                                <span className="text-xs text-green-600">Copied!</span>
                              ) : (
                                <ShareIcon className="w-4 h-4" />
                              )}
                            </button>
                          </PermissionGate>

                          {/* Delete Form */}
                          <PermissionGate module="forms" permission="delete">
                            <button
                              onClick={() => setShowDeleteModal(form)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Form"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Page <span className="font-medium">{currentPage}</span> of{' '}
                    <span className="font-medium">{totalPages}</span>
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3 text-center">
                <ExclamationTriangleIcon className="w-12 h-12 text-red-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Delete Form</h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500">
                    Are you sure you want to delete "{showDeleteModal.name}"? This action cannot be undone.
                  </p>
                </div>
                <div className="flex justify-center space-x-4 mt-4">
                  <button
                    onClick={() => setShowDeleteModal(null)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDeleteForm(showDeleteModal)}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form Builder Modal */}
        <FormBuilder
          form={editingForm}
          onSave={handleSaveForm}
          onClose={handleCloseFormBuilder}
          isOpen={showFormBuilder}
          onBannerChange={(bannerUrl) => setEditingForm((prev) => (prev ? { ...prev, bannerUrl } : prev))}
        />

        {/* Status Update Modal */}
        {showStatusModal && (
          <StatusUpdateModal
            submission={showStatusModal}
            onUpdate={handleStatusUpdate}
            onClose={() => setShowStatusModal(null)}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showSubmissionDeleteModal && (
          <DeleteConfirmationModal
            submission={showSubmissionDeleteModal}
            onDelete={handleDeleteSubmission}
            onClose={() => setShowSubmissionDeleteModal(null)}
          />
        )}

        {/* Review Modal */}
        {showReviewModal && (
          <ReviewModal
            submission={showReviewModal}
            onReview={handleReviewSubmission}
            onClose={() => setShowReviewModal(null)}
          />
        )}

        {/* Submission Modal */}
        {showSubmissionModal && (
          <SubmissionModal
            submission={showSubmissionModal}
            onClose={() => setShowSubmissionModal(null)}
          />
        )}

        {/* Lead Match Modal */}
        {showLeadMatchModal && (
          <LeadMatchModal
            submission={showLeadMatchModal}
            matchingLeads={matchingLeads}
            showCreateOption={showCreateLeadModal}
            onAttachToExisting={handleAttachToExistingLead}
            onCreateNew={handleCreateNewLead}
            onSkip={handleSkipLeadMatching}
            onClose={() => {
              setShowLeadMatchModal(null);
              setMatchingLeads([]);
              setShowCreateLeadModal(false);
            }}
          />
        )}

        {/* Duplicate Email Warning Modal */}
        {showDuplicateEmailModal && duplicateEmailData && (
          <DuplicateEmailModal
            existingLead={duplicateEmailData.existingLead}
            editedEmail={editedEmail}
            onEmailChange={setEditedEmail}
            onContinue={handleDuplicateEmailContinue}
            onAttachToExisting={handleDuplicateEmailAttachToExisting}
            onCancel={() => {
              setShowDuplicateEmailModal(false);
              setDuplicateEmailData(null);
              setEditedEmail('');
            }}
          />
        )}

        {/* Attach to Lead Modal */}
        {showAttachToLeadModal && (
          <AttachToLeadModal
            submission={showAttachToLeadModal}
            leads={filteredLeads}
            searchTerm={leadSearchTerm}
            onSearchChange={setLeadSearchTerm}
            selectedLeadId={selectedLeadId}
            onLeadSelect={setSelectedLeadId}
            onAttach={() => handleAttachToExistingLead(selectedLeadId!)}
            onClose={() => {
              setShowAttachToLeadModal(null);
              setSelectedLeadId(null);
              setLeadSearchTerm('');
            }}
          />
        )}

        {/* Form Modal */}
        {showFormModal && (
          <FormModal
            form={showFormModal}
            onClose={() => setShowFormModal(null)}
          />
        )}

        {/* Bulk Operation Modals */}
        <BulkDeleteModal
          isOpen={showBulkDeleteModal}
          selectedSubmissions={submissions.filter(s => bulkSelection.selectedIds.has(s.id))}
          onConfirm={handleBulkDelete}
          onClose={() => setShowBulkDeleteModal(false)}
        />

        <BulkCreateLeadsModal
          isOpen={showBulkCreateLeadsModal}
          selectedSubmissions={submissions.filter(s => bulkSelection.selectedIds.has(s.id))}
          onConfirm={handleBulkCreateLeads}
          onClose={() => setShowBulkCreateLeadsModal(false)}
        />

        <BulkAttachToLeadModal
          isOpen={showBulkAttachModal}
          selectedSubmissions={submissions.filter(s => bulkSelection.selectedIds.has(s.id))}
          onAttachSingle={handleBulkAttachToSingleLead}
          onAttachIndividual={handleBulkAttachIndividual}
          onClose={() => setShowBulkAttachModal(false)}
          loadLeads={handleLoadLeadsForBulkAttach}
        />

      </div>
    </Layout>
  );
};

// Status Update Modal Component
const StatusUpdateModal: React.FC<{
  submission: FormSubmission;
  onUpdate: (submission: FormSubmission, status: string, notes: string) => void;
  onClose: () => void;
}> = ({ submission, onUpdate, onClose }) => {
  const [status, setStatus] = useState<'submitted' | 'under_review' | 'approved' | 'rejected' | 'attached'>(submission.status);
  const [notes, setNotes] = useState(submission.notes || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdate(submission, status, notes);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <form onSubmit={handleSubmit}>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Update Submission Status</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as 'submitted' | 'under_review' | 'approved' | 'rejected' | 'attached')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="attached">Attached</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Internal Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Add any internal notes..."
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Update Status
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Delete Confirmation Modal Component
const DeleteConfirmationModal: React.FC<{
  submission: FormSubmission;
  onDelete: (submission: FormSubmission) => void;
  onClose: () => void;
}> = ({ submission, onDelete, onClose }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3 text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Delete Submission</h3>
          <div className="mt-2 px-7 py-3">
            <p className="text-sm text-gray-500">
              Are you sure you want to delete this submission from "{submission.displayInfo?.name || 'Anonymous'}"? 
              This action cannot be undone.
            </p>
          </div>
          <div className="flex justify-center space-x-4 mt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              onClick={() => onDelete(submission)}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Review Modal Component
const ReviewModal: React.FC<{
  submission: FormSubmission;
  onReview: (
    submission: FormSubmission,
    action: 'approve' | 'draft' | 'reject',
    reviewedData?: FormSubmissionData,
    changesDescription?: string,
    reviewComments?: string,
    rejectionReason?: string
  ) => void;
  onClose: () => void;
}> = ({ submission, onReview, onClose }) => {
  const [editedData, setEditedData] = useState<FormSubmissionData>(submission.submissionData);
  const [reviewComments, setReviewComments] = useState('');
  const [changesDescription, setChangesDescription] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [showRejectionReason, setShowRejectionReason] = useState(false);

  // Check for changes when data is edited
  React.useEffect(() => {
    const hasDataChanges = JSON.stringify(editedData) !== JSON.stringify(submission.submissionData);
    setHasChanges(hasDataChanges);
  }, [editedData, submission.submissionData]);

  const handleFieldChange = (key: string, value: any) => {
    setEditedData((prev: FormSubmissionData) => ({
      ...prev,
      [key]: value
    }));
  };

  const handleApprove = () => {
    onReview(
      submission,
      'approve',
      hasChanges ? editedData : undefined,
      hasChanges ? changesDescription : undefined,
      reviewComments || undefined
    );
  };

  const handleDraft = () => {
    onReview(
      submission,
      'draft',
      hasChanges ? editedData : undefined,
      hasChanges ? changesDescription : undefined,
      reviewComments || undefined
    );
  };

  const handleReject = () => {
    if (!showRejectionReason) {
      setShowRejectionReason(true);
      return;
    }
    
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    
    onReview(
      submission,
      'reject',
      undefined,
      undefined,
      undefined,
      rejectionReason
    );
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Review Submission</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Submission Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Submission Details</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Submitter:</span> {submission.displayInfo?.name || submission.submitterName || 'Anonymous'}
                </div>
                <div>
                  <span className="font-medium">Email:</span> {submission.displayInfo?.email || submission.submitterEmail}
                </div>
                <div>
                  <span className="font-medium">Form:</span> {submission.formName}
                </div>
                <div>
                  <span className="font-medium">Submitted:</span> {new Date(submission.submittedAt).toLocaleDateString()}
                </div>
              </div>
            </div>

            {/* Editable Form Data */}
            <div>
              <h4 className="font-medium text-gray-900 mb-4">
                Form Data
                {hasChanges && (
                  <span className="ml-2 text-xs text-orange-600 font-normal">
                    (Modified)
                  </span>
                )}
              </h4>
              <div className="space-y-3 max-h-96 overflow-y-auto bg-gray-50 p-4 rounded-lg">
                {Object.entries(editedData).map(([key, value]) => {
                  // Determine input type based on field name
                  const isDate = key.toLowerCase().includes('date') || key.toLowerCase().includes('dob');
                  const isEmail = key.toLowerCase().includes('email');
                  const isPhone = key.toLowerCase().includes('phone') || key.toLowerCase().includes('mobile') || key.toLowerCase().includes('tel');
                  const isUrl = key.toLowerCase().includes('url') || key.toLowerCase().includes('website');
                  const isNumber = key.toLowerCase().includes('year') || key.toLowerCase().includes('age') || key.toLowerCase().includes('result');
                  const isTextArea = key.toLowerCase().includes('notes') || key.toLowerCase().includes('address') || key.toLowerCase().includes('description');
                  const isGender = key.toLowerCase() === 'gender';
                  const isTitle = key.toLowerCase() === 'title';
                  
                  // Format label
                  const label = key
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, l => l.toUpperCase());
                  
                  // Check if value changed
                  const originalValue = submission.submissionData[key];
                  const isChanged = JSON.stringify(value) !== JSON.stringify(originalValue);
                  
                  // Gender options matching FormBuilder template
                  const genderOptions = ['Male', 'Female', 'Other', 'Prefer not to say'];
                  // Title options matching FormBuilder template
                  const titleOptions = ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Prof', 'Rev', 'Sir', 'Dame'];
                  
                  return (
                    <div key={key} className={`bg-white border rounded-lg p-3 ${isChanged ? 'border-orange-300 ring-1 ring-orange-200' : 'border-gray-200'}`}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {label}
                        {isChanged && (
                          <span className="ml-2 text-xs text-orange-600">(modified)</span>
                        )}
                      </label>
                      {isGender ? (
                        <select
                          value={String(value || '')}
                          onChange={(e) => handleFieldChange(key, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        >
                          <option value="">Select Gender</option>
                          {genderOptions.map(option => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : isTitle ? (
                        <select
                          value={String(value || '')}
                          onChange={(e) => handleFieldChange(key, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        >
                          <option value="">Select Title</option>
                          {titleOptions.map(option => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : isTextArea ? (
                        <textarea
                          value={String(value || '')}
                          onChange={(e) => handleFieldChange(key, e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                      ) : (
                        <input
                          type={isDate ? 'date' : isEmail ? 'email' : isPhone ? 'tel' : isUrl ? 'url' : isNumber ? 'number' : 'text'}
                          value={String(value || '')}
                          onChange={(e) => handleFieldChange(key, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                      )}
                      {isChanged && originalValue && (
                        <div className="mt-2 text-xs text-gray-500">
                          Original: <span className="font-mono">{String(originalValue)}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Changes Description - only show if there are changes */}
            {hasChanges && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Changes Description (Optional)
                </label>
                <textarea
                  value={changesDescription}
                  onChange={(e) => setChangesDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe what changes you made..."
                />
              </div>
            )}

            {/* Review Comments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Review Comments (Optional)
              </label>
              <textarea
                value={reviewComments}
                onChange={(e) => setReviewComments(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Add any comments about this submission..."
              />
            </div>

            {/* Rejection Reason - only show when rejecting */}
            {showRejectionReason && (
              <div>
                <label className="block text-sm font-medium text-red-700 mb-2">
                  Reason for Rejection *
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Please explain why this submission is being rejected..."
                  required
                />
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center space-x-2"
              >
                <XCircleIcon className="w-4 h-4" />
                <span>{showRejectionReason ? 'Confirm Rejection' : 'Reject'}</span>
              </button>
              <button
                onClick={handleDraft}
                className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 flex items-center space-x-2"
              >
                <ClockIcon className="w-4 h-4" />
                <span>Save as Draft</span>
              </button>
              <button
                onClick={handleApprove}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center space-x-2"
              >
                <CheckCircleIcon className="w-4 h-4" />
                <span>Approve</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Attach to Lead Modal Component
const AttachToLeadModal: React.FC<{
  submission: FormSubmission;
  leads: any[];
  searchTerm: string;
  onSearchChange: (term: string) => void;
  selectedLeadId: number | null;
  onLeadSelect: (leadId: number) => void;
  onAttach: () => void;
  onClose: () => void;
}> = ({ submission, leads, searchTerm, onSearchChange, selectedLeadId, onLeadSelect, onAttach, onClose }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Attach to Lead</h3>
              <p className="text-sm text-gray-500 mt-1">
                Connect this form submission to an existing lead
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Submission Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Form Submission</h4>
            <p className="text-sm text-gray-700">
              <strong>Name:</strong> {submission.displayInfo?.name || submission.submitterName || 'Anonymous'}
            </p>
            <p className="text-sm text-gray-700">
              <strong>Email:</strong> {submission.displayInfo?.email || submission.submitterEmail}
            </p>
            <p className="text-sm text-gray-700">
              <strong>Form:</strong> {submission.formName}
            </p>
          </div>

          {/* Lead Search */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Leads
            </label>
            <input
              type="text"
              placeholder="Search by name, email, or company..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Leads List */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Lead
            </label>
            <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-md">
              {leads.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {searchTerm ? 'No leads found matching your search' : 'No leads available'}
                </div>
              ) : (
                leads.map((lead) => (
                  <div
                    key={lead.id}
                    onClick={() => onLeadSelect(lead.id)}
                    className={`p-3 border-b border-gray-200 cursor-pointer hover:bg-gray-50 ${
                      selectedLeadId === lead.id ? 'bg-blue-50 border-blue-200' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{lead.fullName}</p>
                        <p className="text-sm text-gray-500">{lead.email}</p>
                        {lead.company && (
                          <p className="text-sm text-gray-500">{lead.company}</p>
                        )}
                      </div>
                      {selectedLeadId === lead.id && (
                        <CheckCircleIcon className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              onClick={onAttach}
              disabled={!selectedLeadId}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Attach to Lead
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Lead Match Modal Component
const LeadMatchModal: React.FC<{
  submission: FormSubmission;
  matchingLeads: any[];
  showCreateOption: boolean;
  onAttachToExisting: (leadId: number) => void;
  onCreateNew: () => void;
  onSkip: () => void;
  onClose: () => void;
}> = ({ submission, matchingLeads, showCreateOption, onAttachToExisting, onCreateNew, onSkip, onClose }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {matchingLeads.length > 0 ? 'Found Matching Leads' : 'No Matching Leads Found'}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {matchingLeads.length > 0 
                  ? 'We found potential matches based on the form data. Choose one to attach this form submission.'
                  : 'No existing leads match this form data. You can create a new lead or search manually.'
                }
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Matching Leads List */}
          {matchingLeads.length > 0 && (
            <div className="mb-6">
              <h4 className="text-md font-medium text-gray-900 mb-3">Matching Leads:</h4>
              <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-md">
                {matchingLeads.map((lead) => (
                  <div
                    key={lead.id}
                    className="p-4 border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
                    onClick={() => onAttachToExisting(lead.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{lead.fullName}</p>
                        <p className="text-sm text-gray-500">{lead.email}</p>
                        {lead.company && (
                          <p className="text-sm text-gray-500">{lead.company}</p>
                        )}
                        {lead.phone && (
                          <p className="text-sm text-gray-500">{lead.phone}</p>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onAttachToExisting(lead.id);
                        }}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                      >
                        Attach
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between pt-4 border-t border-gray-200">
            <div className="flex space-x-3">
              <button
                onClick={onCreateNew}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Create New Lead
              </button>
              <button
                onClick={onSkip}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Search Manually
              </button>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Duplicate Email Warning Modal Component
const DuplicateEmailModal: React.FC<{
  existingLead: any;
  editedEmail: string;
  onEmailChange: (email: string) => void;
  onContinue: () => void;
  onAttachToExisting: () => void;
  onCancel: () => void;
}> = ({ existingLead, editedEmail, onEmailChange, onContinue, onAttachToExisting, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Duplicate Email Detected</h3>
              <p className="text-sm text-gray-500 mt-1">
                A lead with this email address already exists. What would you like to do?
              </p>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Existing Lead Info */}
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <h4 className="text-md font-medium text-yellow-900 mb-2">Existing Lead:</h4>
            <div className="text-sm text-yellow-800">
              <p><strong>Name:</strong> {existingLead.fullName}</p>
              <p><strong>Email:</strong> {existingLead.email}</p>
              {existingLead.company && <p><strong>Company:</strong> {existingLead.company}</p>}
              {existingLead.phone && <p><strong>Phone:</strong> {existingLead.phone}</p>}
            </div>
          </div>

          {/* Email Edit Option */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Change Email Address (Optional)
            </label>
            <input
              type="email"
              value={editedEmail}
              onChange={(e) => onEmailChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter new email address"
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave unchanged to create a new lead with the same email
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between pt-4 border-t border-gray-200">
            <div className="flex space-x-3">
              <button
                onClick={onAttachToExisting}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Attach to Existing Lead
              </button>
              <button
                onClick={onContinue}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Create New Lead
              </button>
            </div>
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Submission Modal Component - Professional Redesign
const SubmissionModal: React.FC<{
  submission: FormSubmission;
  onClose: () => void;
}> = ({ submission, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header - Solid white */}
        <div className="bg-white border-b border-gray-200 px-8 py-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Avatar */}
            <div className="w-16 h-16 rounded-full bg-gray-100 border-2 border-gray-200 flex items-center justify-center text-gray-700 font-bold text-2xl">
              {(submission.displayInfo?.name || submission.submitterName || 'A').charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {submission.displayInfo?.name || submission.submitterName || 'Anonymous'}
              </h2>
              <div className="flex items-center space-x-3 mt-1 text-gray-600 text-sm">
                <span className="flex items-center">
                  <EnvelopeIcon className="w-4 h-4 mr-1.5" />
                  {submission.displayInfo?.email || submission.submitterEmail}
                </span>
                <span>•</span>
                <span className="text-gray-500">#{submission.id}</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors p-2 hover:bg-gray-100 rounded-lg"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Sub-header - Meta Info */}
        <div className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-6 text-sm">
            <div className="flex items-center text-gray-700">
              <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="font-medium">Form:</span>
              <span className="ml-1.5">{submission.formName}</span>
            </div>
            <div className="flex items-center text-gray-700">
              <CalendarIcon className="w-4 h-4 mr-2 text-gray-400" />
              <span className="font-medium">Submitted:</span>
              <span className="ml-1.5">{formatDate(submission.submittedAt)}</span>
            </div>
          </div>
          
          {/* Status Badge - Large */}
          <span className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-bold shadow-sm ${getStatusColor(submission.status)}`}>
            {getStatusIcon(submission.status)}
            <span className="ml-2">{getStatusLabel(submission.status)}</span>
          </span>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          {/* Submission Data */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Form Data
            </h3>
            <div className="bg-gray-50 rounded-lg p-5">
              {renderSubmissionData(submission)}
            </div>
          </div>

          {/* Review Information */}
          {submission.reviewedAt && (
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <CheckCircleIcon className="w-5 h-5 mr-2 text-green-600" />
                Review Information
              </h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-semibold text-blue-900">Reviewed:</span>
                    <span className="ml-2 text-blue-700">{formatDate(submission.reviewedAt)}</span>
                  </div>
                  {submission.reviewerFirstName && submission.reviewerLastName && (
                    <div>
                      <span className="font-semibold text-blue-900">Reviewer:</span>
                      <span className="ml-2 text-blue-700">
                        {submission.reviewerFirstName} {submission.reviewerLastName}
                      </span>
                    </div>
                  )}
                </div>
                {submission.reviewComments && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <p className="font-semibold text-blue-900 text-sm mb-1">Comments:</p>
                    <p className="text-sm text-blue-800">{submission.reviewComments}</p>
                  </div>
                )}
                {submission.changesDescription && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <p className="font-semibold text-blue-900 text-sm mb-1">Changes Made:</p>
                    <p className="text-sm text-blue-800">{submission.changesDescription}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Internal Notes */}
          {submission.notes && (
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
                Internal Notes
              </h3>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
                <p className="text-sm text-amber-900">{submission.notes}</p>
              </div>
            </div>
          )}

          {/* Rejection Reason */}
          {submission.status === 'rejected' && submission.rejectionReason && (
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <XCircleIcon className="w-5 h-5 mr-2 text-red-600" />
                Rejection Reason
              </h3>
              <div className="bg-red-50 border border-red-200 rounded-lg p-5">
                <p className="text-sm text-red-800">{submission.rejectionReason}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-8 py-4 bg-white flex justify-between items-center">
          <div className="text-sm text-gray-600">
            <span className="font-medium">Submission ID:</span> #{submission.id}
            {submission.submitterIp && (
              <span className="ml-4">
                <span className="font-medium">IP:</span> {submission.submitterIp}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium shadow-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Form Modal Component
const FormModal: React.FC<{
  form: Form;
  onClose: () => void;
}> = ({ form, onClose }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900">{form.name}</h3>
              {form.description && (
                <p className="text-sm text-gray-500 mt-1">{form.description}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Form Details */}
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Form Type</label>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {form.formType}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    form.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {form.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Submissions</label>
                <div className="flex items-center">
                  <ChartBarIcon className="w-4 h-4 text-gray-400 mr-1" />
                  <span className="text-sm text-gray-900">{form.submissionCount || 0}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
                <span className="text-sm text-gray-900">{formatDate(form.createdAt)}</span>
              </div>
            </div>

            {/* Form Fields */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Form Fields</label>
              <div className="bg-gray-50 rounded-lg p-4">
                {form.formStructure && form.formStructure.sections && form.formStructure.sections.length > 0 ? (
                  <div className="space-y-4">
                    {form.formStructure.sections.map((section, sectionIndex) => (
                      <div key={sectionIndex}>
                        <h4 className="font-medium text-gray-900 mb-2">{section.title}</h4>
                        {section.fields && section.fields.length > 0 ? (
                          <div className="space-y-2 ml-4">
                            {section.fields.map((field, fieldIndex) => (
                              <div key={fieldIndex} className="flex items-center justify-between p-3 bg-white rounded border">
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900">{field.label}</div>
                                  <div className="text-sm text-gray-500">
                                    {field.type} {field.required && <span className="text-red-500">*</span>}
                                  </div>
                                </div>
                                <div className="text-sm text-gray-500">
                                  {field.placeholder && `"${field.placeholder}"`}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : null}
                        {section.subsections && section.subsections.map((subsection, subIndex) => (
                          <div key={subIndex} className="ml-4">
                            <h5 className="font-medium text-gray-800 mb-2">{subsection.title}</h5>
                            <div className="space-y-2 ml-4">
                              {subsection.fields.map((field, fieldIndex) => (
                                <div key={fieldIndex} className="flex items-center justify-between p-3 bg-white rounded border">
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">{field.label}</div>
                                    <div className="text-sm text-gray-500">
                                      {field.type} {field.required && <span className="text-red-500">*</span>}
                                    </div>
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {field.placeholder && `"${field.placeholder}"`}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No fields defined for this form.</p>
                )}
              </div>
            </div>

            {/* Additional Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Public Access</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  form.isPublic ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {form.isPublic ? 'Public' : 'Private'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Multiple Submissions</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  form.allowMultipleSubmissions ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {form.allowMultipleSubmissions ? 'Allowed' : 'Not Allowed'}
                </span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Forms;
